import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { ProfileProvider } from './profile/ProfileProvider';
import { AutoUpdate } from './components/AutoUpdate';
import { isEmbedded } from './components/SplitView';
import { BackgroundSubmitProvider } from './components/BackgroundSubmit';
import { ErrorBoundary } from './components/ErrorBoundary';
import { purgeOldDrafts } from './lib/drafts';
import { ThemeProvider } from './design/theme';
import { Loading } from './components/Loading';
// Captures Android/Chromium's one-shot beforeinstallprompt before any page mounts.
import './home/installPrompt';
// Self-hosted fonts (offline PWA, no Google request). Italic is for the
// design system's subheadlines.
import '@fontsource-variable/inter/wght.css';
import '@fontsource-variable/inter/wght-italic.css';
import '@fontsource-variable/inter-tight/wght.css';
import './styles/index.css';

// Clean up form drafts older than 14 days. Cheap and runs once per app load.
purgeOldDrafts();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element found in index.html');

// Guest signing (Oct 2026): a new hire without a Workspace account opens their personal signing link.
// No hub sign-in at all, so it renders outside the AuthProvider (its own isolated Firebase app).
const GuestSignPage = lazy(() => import('./signatures/guest/GuestSignPage').then((m) => ({ default: m.GuestSignPage })));
const isGuestSign = window.location.pathname.startsWith('/guest-sign/');

createRoot(rootEl).render(
  isGuestSign ? (
    <StrictMode>
      <ThemeProvider>
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <GuestSignPage />
          </Suspense>
        </ErrorBoundary>
      </ThemeProvider>
    </StrictMode>
  ) :
  <StrictMode>
    <ThemeProvider>
      <ErrorBoundary>
        <BackgroundSubmitProvider>
          <AuthProvider>
            <ProfileProvider>
              <App />
            </ProfileProvider>
          </AuthProvider>
          {/* One updater: not inside the split-view pane. */}
          {!isEmbedded() && <AutoUpdate />}
        </BackgroundSubmitProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </StrictMode>,
);
